import { Box, Link, Typography } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import { useL10n } from '@uplift/hooks';
import { YesNo } from '@uplift/types';
import { Alert, DateOfBirthInput, InsuranceDropzone, InsuranceDropzoneError, LabelInfoTooltip, StandaloneAddressQuickSearchForm, TextInput, YesNoRadioButtonGroup } from '@uplift/ui-v2';
import { useFormikContext } from 'formik';
import React, { useCallback, useMemo, useState } from 'react';
import { NoInsuranceModal } from './no-insurance-modal';
import { InsuranceSelectInput } from './insurance-select-input';
import { InsuranceInfoFormValues } from './validation';

export type InsuranceInfoFormProps = {
  isInviteOrDirectFlow: boolean;
  providerId: string;
};

export const InsuranceInfoForm: React.VFC<InsuranceInfoFormProps> = ({
  providerId,
  isInviteOrDirectFlow,
}) => {
  const l10n = useL10n();
  const { classes, cx } = useInsuranceFormStyles();
  const formik = useFormikContext<InsuranceInfoFormValues>();
  const { errors, values } = formik;
  const [ open, setOpen ] = useState(false);

  const showAlert = useMemo(
    () => [ errors.insuranceCardBack, errors.insuranceCardFront ].some(x => x === InsuranceDropzoneError.FILE_TOO_LARGE),
    [ errors.insuranceCardBack, errors.insuranceCardFront ]
  );

  const primarySubscriberText = (
    <>
      <p>
        {l10n.matching.insurance.primarySubscriberTooltip1}
      </p>
      <p>
        {l10n.matching.insurance.primarySubscriberTooltip2}
      </p>
    </>
  );

  const handleClick = useCallback((e: React.MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault();

    setOpen(true);
  }, []);

  const insuranceHelperText = useMemo(() => {
    if (!isInviteOrDirectFlow) {
      return null;
    }

    return (
      <Link className={classes.link} onClick={handleClick} color="textSecondary" variant="caption">
        {l10n.onboarding.insurance.providerInsuranceHint}
      </Link>
    );
  }, [ classes.link, handleClick, isInviteOrDirectFlow, l10n.onboarding.insurance.providerInsuranceHint ]);

  return (
    <div>
      <Box display="flex" flexDirection="column" gap="1.5rem">
        <Box>
          <YesNoRadioButtonGroup
            label={(
              <LabelInfoTooltip
                label={l10n.matching.insurance.arePrimarySubscriber}
                tooltipTitle={primarySubscriberText}
              />
            )}
            name="isPrimarySubscriber"
          />
        </Box>
        <Box display="flex" flexDirection="column">
          <InsuranceSelectInput
            name="insuranceProvider"
            label={l10n.matching.insurance.insuranceProvider}
            className={classes.formControl75}
            disabled={!isInviteOrDirectFlow}
            providerId={providerId}
          />
          {insuranceHelperText}
        </Box>
        <TextInput
          fullWidth
          id="member-id"
          name="memberId"
          label={l10n.matching.insurance.memberId}
          className={cx(classes.formControl75, 'fs-exclude')}
        />
        <TextInput
          fullWidth
          id="group-number"
          name="groupNumber"
          label={l10n.matching.insurance.groupNumber}
          className={cx(classes.formControl75, 'fs-exclude')}
        />
        <Typography variant="overline" color="secondary" component="p">
          {l10n.matching.insurance.insuranceCard}
        </Typography>
        {showAlert && (
          <Alert className={classes.alert} severity="error">{l10n.accountSettings.insurance.insuranceCardDropRejected}</Alert>
        )}
        <InsuranceDropzone
          name="insuranceCardFront"
          placeholderText={l10n.matching.insurance.uploadFrontSide}
          altText="front side"
          color="secondary"
        />
        <InsuranceDropzone
          name="insuranceCardBack"
          placeholderText={l10n.matching.insurance.uploadBackSide}
          altText="back side"
          color="secondary"
        />
        {values.isPrimarySubscriber === YesNo.No && (
          <>
            <Typography variant="overline" color="secondary" component="p">
              {l10n.matching.insurance.primarySub}
            </Typography>
            <TextInput
              fullWidth
              id="primary-subscriber-first-name"
              name="primarySubscriberFirstName"
              label={l10n.onboarding.account.firstName}
              className={classes.formControl75}
            />
            <TextInput
              fullWidth
              id="primary-subscriber-last-name"
              name="primarySubscriberLastName"
              label={l10n.onboarding.account.lastName}
              className={classes.formControl75}
            />
            <DateOfBirthInput
              id="primary-subscriber-date-of-birth"
              name="primarySubscriberDateOfBirth"
              label={l10n.onboarding.account.dateOfBirth}
              helperText={l10n.matching.insurance.dateOfBirthTip}
              className={classes.dob}
              inputClasses={{ root: classes.dobInput }}
            />
            <StandaloneAddressQuickSearchForm
              label={l10n.matching.insurance.primarySubAddress}
              getFieldName={name => `address.${name}`}
            />
          </>
        )}
      </Box>
      <NoInsuranceModal open={open} onClose={() => setOpen(false)} providerId={providerId} />
    </div>
  );
};


const useInsuranceFormStyles = makeStyles({ name: 'InsuranceForm' })(theme => ({
  link: {
    color: theme.palette.text.secondary,
    cursor: 'pointer',
    textDecoration: 'underline',
    textTransform: 'none',
    '&:hover': {
      color: theme.palette.text.secondary,
      textDecoration: 'none',
    }
  },
  formControl75: {
    width: '100%',
    [theme.breakpoints.up('sm')]: {
      width: '75%',
    }
  },
  alert: {
    marginBottom: theme.spacing(1)
  },
  dob: {
    display: 'flex',
    marginBottom: theme.spacing(3),
  },
  dobInput: {
    width: '120px',
  }
}));
